import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { CalendarDays, Droplets, Gem, PartyPopper, Sparkles, Wind } from 'lucide-react'
import { useStoreGate } from '@/contexts/StoreGateContext'
import {
  formatCountdownParts,
  hasCompletedLaunchCelebration,
  isLaunchGateConfigured,
  markLaunchCelebrationComplete,
  parseLaunchAt,
} from '@/lib/launchGate'
import { LOGO_ALT, LOGO_ICON_PATH, STORE_NAME, STORE_TAGLINE } from '@/lib/brand'
import { formatDateTime } from '@/lib/utils'
import { StoreImage } from '@/store/components/StoreImage'
import { homeEase } from '@/store/components/home/motion'

const LIVE_COUNTDOWN_SECONDS = 10
const LIVE_RING_RADIUS = 54
const LIVE_RING_CIRCUMFERENCE = 2 * Math.PI * LIVE_RING_RADIUS
const LAUNCH_PROGRESS_WINDOW_MS = 30 * 24 * 60 * 60 * 1000

const LAUNCH_SUBTITLE =
  'Discover handcrafted attars — rich, long-lasting, and unmistakably luxurious.'

type LaunchPhase = 'countdown' | 'live'

const LAUNCH_HIGHLIGHTS = [
  { icon: Gem, label: 'Premium attars' },
  { icon: Wind, label: 'Long-lasting' },
  { icon: Droplets, label: 'Pure oils' },
] as const

const COUNTDOWN_UNITS = [
  { key: 'days', label: 'Days' },
  { key: 'hours', label: 'Hours' },
  { key: 'minutes', label: 'Minutes' },
  { key: 'seconds', label: 'Seconds' },
] as const

const FLOATING_PARTICLES = Array.from({ length: 22 }, (_, index) => ({
  id: index,
  left: `${6 + ((index * 19) % 88)}%`,
  top: `${4 + ((index * 27) % 92)}%`,
  size: index % 4 === 0 ? 5 : index % 4 === 1 ? 3 : 2,
  delay: (index % 7) * 0.35,
  duration: 7 + (index % 6) * 1.4,
}))

function LaunchParticles({ active }: { active: boolean }) {
  const reduceMotion = useReducedMotion()
  if (reduceMotion) return null

  return (
    <div className="launch-page-particles" aria-hidden>
      {FLOATING_PARTICLES.map((particle) => (
        <motion.span
          key={particle.id}
          className="launch-page-particle"
          style={{
            left: particle.left,
            top: particle.top,
            width: particle.size,
            height: particle.size,
          }}
          animate={
            active
              ? {
                  y: [0, -36, 0],
                  x: [0, indexOffset(particle.id), 0],
                  opacity: [0.2, 1, 0.2],
                  scale: [1, 1.5, 1],
                }
              : {
                  y: [0, -22, 0],
                  opacity: [0.06, 0.55, 0.06],
                  scale: [1, 1.2, 1],
                }
          }
          transition={{
            duration: particle.duration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: particle.delay,
          }}
        />
      ))}
    </div>
  )
}

function indexOffset(id: number) {
  return id % 2 === 0 ? 14 : -14
}

function CountdownUnit({
  label,
  value,
  reduceMotion,
}: {
  label: string
  value: number
  reduceMotion: boolean | null
}) {
  const display = String(value).padStart(2, '0')

  return (
    <div className="launch-countdown-unit">
      <span className="launch-countdown-unit-accent" aria-hidden />
      <div className="launch-countdown-unit-inner">
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={display}
            initial={
              reduceMotion
                ? false
                : { y: 20, opacity: 0, filter: 'blur(8px)' }
            }
            animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
            exit={
              reduceMotion
                ? undefined
                : { y: -20, opacity: 0, filter: 'blur(8px)' }
            }
            transition={{ duration: 0.35, ease: homeEase }}
            className="launch-countdown-value"
          >
            {display}
          </motion.span>
        </AnimatePresence>
      </div>
      <span className="launch-countdown-label">{label}</span>
    </div>
  )
}

const LIVE_CONFETTI = Array.from({ length: 14 }, (_, index) => ({
  id: index,
  angle: (index / 14) * 360,
  distance: 72 + (index % 4) * 18,
  size: index % 3 === 0 ? 6 : index % 3 === 1 ? 4 : 3,
  delay: (index % 5) * 0.04,
}))

function LiveConfettiBurst({ reduceMotion }: { reduceMotion: boolean | null }) {
  if (reduceMotion) return null

  return (
    <div className="launch-live-confetti" aria-hidden>
      {LIVE_CONFETTI.map((piece) => {
        const rad = (piece.angle * Math.PI) / 180
        const x = Math.cos(rad) * piece.distance
        const y = Math.sin(rad) * piece.distance

        return (
          <motion.span
            key={piece.id}
            className="launch-live-confetti-piece"
            style={{ width: piece.size, height: piece.size }}
            initial={{ opacity: 0, x: 0, y: 0, scale: 0 }}
            animate={{
              opacity: [0, 1, 0.85, 0],
              x: [0, x * 0.55, x],
              y: [0, y * 0.55, y],
              scale: [0, 1.2, 0.6],
              rotate: [0, piece.angle],
            }}
            transition={{
              duration: 1.35,
              ease: [0.22, 1, 0.36, 1],
              delay: piece.delay,
            }}
          />
        )
      })}
    </div>
  )
}

function LiveSparkleField({ reduceMotion }: { reduceMotion: boolean | null }) {
  if (reduceMotion) return null

  return (
    <div className="launch-live-sparkles" aria-hidden>
      {[0, 1, 2, 3, 4, 5].map((id) => (
        <motion.span
          key={id}
          className="launch-live-sparkle"
          style={{
            left: `${12 + id * 15}%`,
            top: `${18 + (id % 3) * 28}%`,
          }}
          animate={{
            opacity: [0.15, 0.9, 0.15],
            scale: [0.6, 1.15, 0.6],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 2.4 + id * 0.3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: id * 0.2,
          }}
        >
          <Sparkles className="h-3.5 w-3.5 text-gold-500" />
        </motion.span>
      ))}
    </div>
  )
}

function LiveCountdownRing({
  liveSeconds,
  reduceMotion,
}: {
  liveSeconds: number
  reduceMotion: boolean | null
}) {
  const progress = liveSeconds / LIVE_COUNTDOWN_SECONDS
  const offset = LIVE_RING_CIRCUMFERENCE * (1 - progress)
  const elapsed = LIVE_COUNTDOWN_SECONDS - liveSeconds

  return (
    <div className="launch-page-live-ring-wrap">
      {!reduceMotion && (
        <>
          <motion.span
            className="launch-page-live-orbit launch-page-live-orbit--outer"
            animate={{ rotate: 360 }}
            transition={{ duration: 24, repeat: Infinity, ease: 'linear' }}
            aria-hidden
          />
          <motion.span
            className="launch-page-live-orbit launch-page-live-orbit--inner"
            animate={{ rotate: -360 }}
            transition={{ duration: 16, repeat: Infinity, ease: 'linear' }}
            aria-hidden
          />
        </>
      )}
      <motion.div
        className="launch-page-live-ring-glow"
        animate={
          reduceMotion
            ? undefined
            : { scale: [1, 1.08, 1], opacity: [0.55, 0.95, 0.55] }
        }
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        aria-hidden
      />
      <svg className="launch-page-live-ring" viewBox="0 0 120 120" aria-hidden>
        <defs>
          <linearGradient id="launch-ring-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f9e076" />
            <stop offset="45%" stopColor="#d4af37" />
            <stop offset="100%" stopColor="#c5a059" />
          </linearGradient>
          <filter id="launch-ring-glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {Array.from({ length: 12 }, (_, i) => {
          const angle = (i / 12) * 360 - 90
          const rad = (angle * Math.PI) / 180
          const x1 = 60 + Math.cos(rad) * 48
          const y1 = 60 + Math.sin(rad) * 48
          const x2 = 60 + Math.cos(rad) * 52
          const y2 = 60 + Math.sin(rad) * 52
          return (
            <line
              key={i}
              className="launch-page-live-tick"
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
            />
          )
        })}
        <circle
          className="launch-page-live-ring-track"
          cx="60"
          cy="60"
          r={LIVE_RING_RADIUS}
        />
        <motion.circle
          className="launch-page-live-ring-progress"
          cx="60"
          cy="60"
          r={LIVE_RING_RADIUS}
          strokeDasharray={LIVE_RING_CIRCUMFERENCE}
          animate={{ strokeDashoffset: offset }}
          transition={
            reduceMotion ? { duration: 0 } : { duration: 0.85, ease: homeEase }
          }
          filter="url(#launch-ring-glow)"
        />
      </svg>
      <div className="launch-page-live-center">
        <AnimatePresence mode="wait">
          <motion.p
            key={liveSeconds}
            initial={reduceMotion ? false : { opacity: 0, y: 22, scale: 0.5, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
            exit={reduceMotion ? undefined : { opacity: 0, y: -18, scale: 1.1, filter: 'blur(6px)' }}
            transition={{ duration: 0.42, ease: homeEase }}
            className="launch-page-live-number"
            aria-live="polite"
          >
            {liveSeconds}
          </motion.p>
        </AnimatePresence>
        <span className="launch-page-live-unit">sec</span>
      </div>
      {!reduceMotion && (
        <motion.span
          className="launch-page-live-burst"
          key={elapsed}
          initial={{ opacity: 0.7, scale: 0.85 }}
          animate={{ opacity: 0, scale: 1.55 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          aria-hidden
        />
      )}
    </div>
  )
}

function LiveCelebrationPanel({
  storeName,
  liveSeconds,
  reduceMotion,
}: {
  storeName: string
  liveSeconds: number
  reduceMotion: boolean | null
}) {
  const enterProgress =
    ((LIVE_COUNTDOWN_SECONDS - liveSeconds) / LIVE_COUNTDOWN_SECONDS) * 100

  return (
    <div className="launch-live-celebration">
      <LiveConfettiBurst reduceMotion={reduceMotion} />
      <LiveSparkleField reduceMotion={reduceMotion} />

      <motion.div
        className="launch-live-badge"
        initial={reduceMotion ? false : { opacity: 0, y: -12, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.55, ease: homeEase }}
      >
        <span className="launch-live-badge-dot" aria-hidden />
        <PartyPopper className="h-3.5 w-3.5 text-gold-700" aria-hidden />
        Now open
      </motion.div>

      <motion.h2
        className="launch-page-live-kicker"
        initial={reduceMotion ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.08, duration: 0.65, ease: homeEase }}
      >
        We&apos;re live
      </motion.h2>

      <motion.p
        className="launch-page-live-subtitle"
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.16, duration: 0.55, ease: homeEase }}
      >
        Welcome to <span className="launch-page-live-brand">{storeName}</span>
      </motion.p>

      <motion.div
        initial={reduceMotion ? false : { opacity: 0, scale: 0.82 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.24, duration: 0.7, ease: homeEase }}
      >
        <LiveCountdownRing liveSeconds={liveSeconds} reduceMotion={reduceMotion} />
      </motion.div>

      <motion.div
        className="launch-live-enter-track"
        initial={reduceMotion ? false : { opacity: 0, width: 0 }}
        animate={{ opacity: 1, width: '100%' }}
        transition={{ delay: 0.35, duration: 0.6, ease: homeEase }}
        aria-hidden
      >
        <motion.span
          className="launch-live-enter-fill"
          initial={false}
          animate={{ width: `${enterProgress}%` }}
          transition={{ duration: 0.85, ease: homeEase }}
        />
      </motion.div>

      <motion.p
        className="launch-page-panel-note launch-page-panel-note--live"
        initial={reduceMotion ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.42, duration: 0.5 }}
      >
        Opening the storefront in{' '}
        <strong className="launch-live-count-highlight">{liveSeconds}</strong>
        <span className="launch-live-ellipsis" aria-hidden>
          <span>.</span>
          <span>.</span>
          <span>.</span>
        </span>
      </motion.p>
    </div>
  )
}

export function LaunchPage() {
  const navigate = useNavigate()
  const { settings, status } = useStoreGate()
  const reduceMotion = useReducedMotion()

  const launchAtIso = settings?.launchAt?.trim() || ''
  const launchAtMs = useMemo(() => parseLaunchAt(launchAtIso), [launchAtIso])

  const [now, setNow] = useState(() => Date.now())
  const [phase, setPhase] = useState<LaunchPhase>('countdown')
  const [liveSeconds, setLiveSeconds] = useState(LIVE_COUNTDOWN_SECONDS)
  /** Only users who loaded the page before launch see the go-live celebration. */
  const wasWaitingForLaunchRef = useRef(
    launchAtMs != null && Date.now() < launchAtMs
  )

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    if (status === 'loading') return

    if (!isLaunchGateConfigured(settings)) {
      navigate('/', { replace: true })
      return
    }

    if (hasCompletedLaunchCelebration(launchAtIso)) {
      navigate('/', { replace: true })
      return
    }

    // Late visitor after launch — skip "We're live" and open the store.
    if (launchAtMs != null && Date.now() >= launchAtMs) {
      markLaunchCelebrationComplete(launchAtIso)
      navigate('/', { replace: true })
    }
  }, [status, settings, launchAtIso, launchAtMs, navigate])

  useEffect(() => {
    if (launchAtMs == null) return
    if (phase !== 'countdown') return
    if (now < launchAtMs) return
    if (!wasWaitingForLaunchRef.current) return

    setPhase('live')
    setLiveSeconds(LIVE_COUNTDOWN_SECONDS)
  }, [phase, now, launchAtMs])

  useEffect(() => {
    if (phase !== 'live') return
    if (liveSeconds <= 0) {
      markLaunchCelebrationComplete(launchAtIso)
      navigate('/', { replace: true })
      return
    }
    const id = window.setTimeout(() => {
      setLiveSeconds((current) => current - 1)
    }, 1000)
    return () => clearTimeout(id)
  }, [phase, liveSeconds, launchAtIso, navigate])

  if (status === 'loading' || !isLaunchGateConfigured(settings) || launchAtMs == null) {
    return <div className="launch-page launch-page--loading" aria-hidden />
  }

  const remainingMs = Math.max(0, launchAtMs - now)
  const countdown = formatCountdownParts(remainingMs)
  const countdownValues = {
    days: countdown.days,
    hours: countdown.hours,
    minutes: countdown.minutes,
    seconds: countdown.seconds,
  }
  const storeName = settings?.storeName?.trim() || STORE_NAME
  const tagline = settings?.tagline?.trim() || STORE_TAGLINE
  const isLive = phase === 'live'
  const launchDateLabel = formatDateTime(launchAtIso)
  const launchProgress = Math.min(
    100,
    Math.max(0, (1 - remainingMs / LAUNCH_PROGRESS_WINDOW_MS) * 100)
  )
  const showLaunchProgress = remainingMs <= LAUNCH_PROGRESS_WINDOW_MS

  return (
    <div className={`launch-page${isLive ? ' launch-page--live' : ''}`}>
      <div className="launch-page-ambience" aria-hidden>
        <div className="launch-page-gradient" />
        <div className="launch-page-noise" />
        <div className="launch-page-vignette" />
        {!reduceMotion && (
          <>
            <motion.div
              className="launch-page-glow launch-page-glow--a"
              animate={{ x: [0, 56, 0], y: [0, 32, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="launch-page-glow launch-page-glow--b"
              animate={{ x: [0, -44, 0], y: [0, 26, 0], scale: [1, 1.14, 1] }}
              transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
            />
            <motion.div
              className="launch-page-glow launch-page-glow--c"
              animate={{ scale: [0.9, 1.22, 0.9], opacity: [0.3, 0.7, 0.3] }}
              transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
            />
            <motion.div
              className="launch-page-ray"
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 52, repeat: Infinity, ease: 'linear' }}
            />
            <motion.div
              className="launch-page-arc launch-page-arc--tl"
              animate={{ opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="launch-page-arc launch-page-arc--br"
              animate={{ opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            />
          </>
        )}
        <div className="launch-page-grid" />
        <LaunchParticles active={isLive} />
      </div>

      <div className="launch-page-shell">
        <div className="launch-page-layout">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, x: -28 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.75, ease: homeEase }}
            className="launch-page-brand"
          >
            <motion.div
              className="launch-page-eyebrow"
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.5, ease: homeEase }}
            >
              <Sparkles className="h-3.5 w-3.5 text-gold-600" aria-hidden />
              {tagline}
            </motion.div>

            <div className="launch-page-logo-shell">
              {!reduceMotion && (
                <>
                  <motion.span
                    className="launch-page-logo-ring launch-page-logo-ring--outer"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 32, repeat: Infinity, ease: 'linear' }}
                    aria-hidden
                  />
                  <motion.span
                    className="launch-page-logo-ring launch-page-logo-ring--mid"
                    animate={{ rotate: -360 }}
                    transition={{ duration: 22, repeat: Infinity, ease: 'linear' }}
                    aria-hidden
                  />
                  <span className="launch-page-logo-halo" aria-hidden />
                </>
              )}
              <motion.div
                className="launch-page-logo-mark"
                animate={
                  reduceMotion ? undefined : { y: [0, -8, 0], scale: [1, 1.03, 1] }
                }
                transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <div className="launch-page-logo-mark-inner">
                  <StoreImage
                    src={LOGO_ICON_PATH}
                    alt={LOGO_ALT}
                    priority
                    objectFit="contain"
                    sizing="box"
                    spinnerSize="sm"
                    containerClassName="launch-page-logo"
                    className="h-full w-full object-contain"
                  />
                </div>
              </motion.div>
            </div>

            <motion.h1
              className="launch-page-title type-hero"
              initial={reduceMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.16, duration: 0.65, ease: homeEase }}
            >
              {storeName}
            </motion.h1>

            <motion.p
              className="launch-page-lead type-body-muted"
              initial={reduceMotion ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.24, duration: 0.55, ease: homeEase }}
            >
              {LAUNCH_SUBTITLE}
            </motion.p>

            <motion.div
              className="launch-page-footer launch-page-footer--brand"
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.34, duration: 0.55, ease: homeEase }}
            >
              {LAUNCH_HIGHLIGHTS.map(({ icon: Icon, label }) => (
                <span key={label} className="launch-page-chip">
                  <Icon className="h-3.5 w-3.5 text-gold-600" aria-hidden />
                  {label}
                </span>
              ))}
            </motion.div>
          </motion.div>

          <AnimatePresence mode="wait">
            {phase === 'countdown' ? (
              <motion.div
                key="countdown"
                initial={reduceMotion ? false : { opacity: 0, x: 28, scale: 0.97 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={reduceMotion ? undefined : { opacity: 0, x: -20, scale: 0.97 }}
                transition={{ duration: 0.55, ease: homeEase }}
                className="launch-page-card"
              >
                <div className="launch-page-card-shine" aria-hidden />
                <div className="launch-page-card-inner">
                  <div className="launch-page-panel-header">
                    <span className="launch-page-panel-badge">
                      <span className="launch-page-panel-badge-dot" aria-hidden />
                      Launching soon
                    </span>
                    <p className="launch-page-panel-kicker">
                      The countdown has begun
                    </p>
                  </div>

                  <div className="launch-countdown-row">
                    {COUNTDOWN_UNITS.map((unit, index) => (
                      <div key={unit.key} className="launch-countdown-item">
                        <CountdownUnit
                          label={unit.label}
                          value={countdownValues[unit.key]}
                          reduceMotion={reduceMotion}
                        />
                        {index < COUNTDOWN_UNITS.length - 1 && (
                          <span className="launch-countdown-sep" aria-hidden>
                            :
                          </span>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="launch-page-date-chip">
                    <CalendarDays className="h-4 w-4 text-gold-600" aria-hidden />
                    <span>{launchDateLabel}</span>
                  </div>

                  {showLaunchProgress && (
                    <div className="launch-page-progress-block">
                      <div className="launch-page-progress-meta">
                        <span>Launch progress</span>
                        <span>{Math.round(launchProgress)}%</span>
                      </div>
                      <div className="launch-page-progress">
                        <motion.span
                          className="launch-page-progress-fill"
                          initial={false}
                          animate={{ width: `${launchProgress}%` }}
                          transition={{ duration: 0.9, ease: homeEase }}
                        />
                      </div>
                    </div>
                  )}

                  <p className="launch-page-panel-note type-body-muted">
                    We&apos;re putting the finishing touches on your next favourite fragrance.
                  </p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="live"
                initial={reduceMotion ? false : { opacity: 0, scale: 0.88, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, scale: 1.05 }}
                transition={{ duration: 0.7, ease: homeEase }}
                className="launch-page-card launch-page-card--live"
              >
                <div className="launch-page-card-shine launch-page-card-shine--live" aria-hidden />
                {!reduceMotion && (
                  <motion.div
                    className="launch-live-card-flash"
                    initial={{ opacity: 0.65 }}
                    animate={{ opacity: 0 }}
                    transition={{ duration: 1.1, ease: 'easeOut' }}
                    aria-hidden
                  />
                )}
                <div className="launch-page-card-inner launch-page-card-inner--live">
                  <LiveCelebrationPanel
                    storeName={storeName}
                    liveSeconds={liveSeconds}
                    reduceMotion={reduceMotion}
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
